import * as React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import MicNoneIcon from '@mui/icons-material/MicNone'

import {
    VOICE_SOURCES,
    isCallSource,
    isCallCaptureAvailable,
    platformHint,
} from '../utils/voiceSource'

const pad = value => String(value).padStart(2, '0')

// Same "speech_" family as the "Speech to graph" cards, but readable.
export const defaultVoiceGraphName = (now = new Date()) =>
    `speech_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
    )}_${pad(now.getHours())}-${pad(now.getMinutes())}`

// `?speech=1` is the existing contract FirstEntry honours: no import dialog,
// statements panel open, voice input on the given source (`mic=0` leaves the
// microphone out of a call capture).
export const voiceGraphUrl = (
    username,
    name,
    { source = VOICE_SOURCES.MIC, includeMic = true } = {}
) => {
    const query = new URLSearchParams({ speech: '1', source })
    if (isCallSource(source) && !includeMic) query.set('mic', '0')
    return `/${username}/${encodeURIComponent(name)}/edit?${query}`
}

const SOURCE_OPTIONS = [
    { source: VOICE_SOURCES.MIC, label: 'Microphone' },
    { source: VOICE_SOURCES.TAB, label: 'Call or video in a browser tab' },
    { source: VOICE_SOURCES.SCREEN, label: 'Computer audio (screen share)' },
]

/**
 * Names a new graph and picks where its voice input listens, then opens it.
 */
export default function VoiceGraphDialog({ open, onClose, onStart }) {
    const [name, setName] = React.useState(defaultVoiceGraphName)
    const [source, setSource] = React.useState(VOICE_SOURCES.MIC)
    const [includeMic, setIncludeMic] = React.useState(true)
    const callCaptureAvailable = isCallCaptureAvailable()

    React.useEffect(() => {
        if (open) setName(defaultVoiceGraphName())
    }, [open])

    const trimmed = name.trim().replace(/\s+/g, ' ')
    const callSource = isCallSource(source)

    const handleStart = () => {
        if (!trimmed) return
        onStart({ name: trimmed, source, includeMic })
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Speak your graph</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Every pause adds what was said as a statement of the new
                    graph.
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    label="Graph name"
                    value={name}
                    onChange={event => setName(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            handleStart()
                        }
                    }}
                    inputProps={{ 'data-testid': 'voice-graph-name' }}
                />
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mt: 2.5, mb: 0.5 }}
                >
                    Listen to
                </Typography>
                <RadioGroup
                    value={source}
                    onChange={event => setSource(event.target.value)}
                >
                    {SOURCE_OPTIONS.map(option => (
                        <FormControlLabel
                            key={option.source}
                            value={option.source}
                            label={option.label}
                            disabled={
                                isCallSource(option.source) &&
                                !callCaptureAvailable
                            }
                            control={
                                <Radio
                                    size="small"
                                    inputProps={{
                                        'data-testid': `voice-graph-source-${option.source}`,
                                    }}
                                />
                            }
                        />
                    ))}
                </RadioGroup>
                {callSource && (
                    <>
                        <FormControlLabel
                            sx={{ ml: 0 }}
                            label="Include my microphone (my side is tagged speaker: me)"
                            control={
                                <Switch
                                    size="small"
                                    checked={includeMic}
                                    onChange={event =>
                                        setIncludeMic(event.target.checked)
                                    }
                                    inputProps={{
                                        'data-testid': 'voice-graph-include-mic',
                                    }}
                                />
                            }
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1 }}
                        >
                            {platformHint(source)} The graph opens first; one
                            click on its microphone then starts the sharing.
                            Let participants know the call is transcribed.
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="info"
                    disabled={!trimmed}
                    startIcon={<MicNoneIcon />}
                    onClick={handleStart}
                    data-testid="voice-graph-start"
                >
                    Start
                </Button>
            </DialogActions>
        </Dialog>
    )
}
